import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, CheckCircle, Clock, Info, Share2, Tag, Ticket, Users, Award, Briefcase, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
export function BenefitDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  // Mock data for the specific benefit
  const benefit = {
    id: Number(id) || 1,
    title: 'Diskon Pelatihan & Workshop',
    category: 'learning',
    description: 'Dapatkan diskon eksklusif hingga 50% untuk berbagai pelatihan, workshop, dan sertifikasi yang diselenggarakan oleh ATIOS dan mitra resmi kami. Tingkatkan keahlian profesional Anda dengan biaya yang lebih terjangkau.',
    discount: '50%',
    validUntil: '31 Desember 2025',
    usageLimit: '1x per bulan',
    terms: ['Berlaku untuk semua anggota aktif ATIOS', 'Kode voucher hanya dapat digunakan satu kali per transaksi', 'Tidak dapat digabungkan dengan promo lainnya', 'Berlaku untuk pendaftaran online melalui website ATIOS', 'Wajib menunjukkan kartu anggota digital saat verifikasi'],
    howToUse: ['Klik tombol "Klaim Benefit" di bawah ini', 'Dapatkan kode voucher unik Anda', 'Masukkan kode voucher saat checkout pendaftaran event/training', 'Diskon akan otomatis terpotong dari total biaya'],
    provider: 'ATIOS Academy',
    status: 'available'
  };
  // Mock related benefits
  const relatedBenefits = [{
    id: 2,
    title: 'Akses Bursa Lowongan Premium',
    category: 'career',
    icon: Briefcase,
    discount: 'Gratis'
  }, {
    id: 4,
    title: 'Acara Networking Bulanan',
    category: 'networking',
    icon: Users,
    discount: 'Gratis'
  }, {
    id: 1,
    title: 'Sertifikat Keanggotaan',
    category: 'membership',
    icon: Award,
    discount: 'Gratis'
  }];
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'learning':
        return BookOpen;
      case 'career':
        return Briefcase;
      case 'networking':
        return Users;
      default:
        return Award;
    }
  };
  const Icon = getCategoryIcon(benefit.category);
  return <div className="min-h-screen bg-white" data-id="element-1116">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1117">
        {/* Header */}
        <div className="mb-8" data-id="element-1118">
          <Button variant="ghost" onClick={() => navigate('/benefits')} className="mb-4 pl-0 hover:pl-2 transition-all" data-id="element-1119">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1120" />
            Kembali ke Daftar Manfaat
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1121">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-1122">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-atios-light to-white rounded-2xl p-8 border border-atios-primary/10" data-id="element-1123">
              <div className="flex items-start justify-between mb-6" data-id="element-1124">
                <div className="h-16 w-16 rounded-xl bg-white shadow-sm flex items-center justify-center" data-id="element-1125">
                  <Icon className="h-8 w-8 text-atios-primary" data-id="element-1126" />
                </div>
                <div className="flex gap-2" data-id="element-1127">
                  <Badge variant="primary" className="capitalize" data-id="element-1128">
                    {benefit.category}
                  </Badge>
                  <Badge variant="success" data-id="element-1129">Tersedia</Badge>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-neutral-900 mb-4" data-id="element-1130">
                {benefit.title}
              </h1>
              <p className="text-neutral-600 text-lg leading-relaxed" data-id="element-1131">
                {benefit.description}
              </p>
            </div>

            {/* Terms & Conditions */}
            <Card data-id="element-1132">
              <CardHeader data-id="element-1133">
                <CardTitle className="flex items-center gap-2" data-id="element-1134">
                  <Info className="w-5 h-5 text-atios-primary" data-id="element-1135" />
                  Syarat & Ketentuan
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-1136">
                <ul className="space-y-3" data-id="element-1137">
                  {benefit.terms.map((term, index) => <li key={index} className="flex items-start gap-3 text-neutral-700" data-id="element-1138">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-1139" />
                      <span data-id="element-1140">{term}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* How to Use */}
            <Card data-id="element-1141">
              <CardHeader data-id="element-1142">
                <CardTitle className="flex items-center gap-2" data-id="element-1143">
                  <Ticket className="w-5 h-5 text-atios-primary" data-id="element-1144" />
                  Cara Penggunaan
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-1145">
                <div className="space-y-6" data-id="element-1146">
                  {benefit.howToUse.map((step, index) => <div key={index} className="flex gap-4" data-id="element-1147">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-atios-light text-atios-primary font-bold flex items-center justify-center" data-id="element-1148">
                        {index + 1}
                      </div>
                      <p className="text-neutral-700 mt-1" data-id="element-1149">{step}</p>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-1150">
            {/* Benefit Info Card */}
            <Card className="sticky top-8" data-id="element-1151">
              <CardContent className="p-6 space-y-6" data-id="element-1152">
                <div className="text-center pb-6 border-b border-neutral-200" data-id="element-1153">
                  <p className="text-sm text-neutral-500 mb-1" data-id="element-1154">Nilai Manfaat</p>
                  <p className="text-3xl font-bold text-atios-primary" data-id="element-1155">
                    {benefit.discount}
                  </p>
                </div>

                <div className="space-y-4" data-id="element-1156">
                  <div className="flex justify-between items-center" data-id="element-1157">
                    <div className="flex items-center gap-2 text-neutral-600" data-id="element-1158">
                      <Calendar className="w-4 h-4" data-id="element-1159" />
                      <span className="text-sm" data-id="element-1160">Berlaku Hingga</span>
                    </div>
                    <span className="text-sm font-medium text-neutral-900" data-id="element-1161">
                      {benefit.validUntil}
                    </span>
                  </div>

                  <div className="flex justify-between items-center" data-id="element-1162">
                    <div className="flex items-center gap-2 text-neutral-600" data-id="element-1163">
                      <Clock className="w-4 h-4" data-id="element-1164" />
                      <span className="text-sm" data-id="element-1165">Batas Penggunaan</span>
                    </div>
                    <span className="text-sm font-medium text-neutral-900" data-id="element-1166">
                      {benefit.usageLimit}
                    </span>
                  </div>

                  <div className="flex justify-between items-center" data-id="element-1167">
                    <div className="flex items-center gap-2 text-neutral-600" data-id="element-1168">
                      <Tag className="w-4 h-4" data-id="element-1169" />
                      <span className="text-sm" data-id="element-1170">Penyedia</span>
                    </div>
                    <span className="text-sm font-medium text-neutral-900" data-id="element-1171">
                      {benefit.provider}
                    </span>
                  </div>
                </div>

                <div className="pt-4 space-y-3" data-id="element-1172">
                  <Button className="w-full" size="lg" onClick={() => navigate(`/benefits/${benefit.id}/claim`)} data-id="element-1173">
                    Klaim Benefit
                  </Button>
                  <Button variant="secondary" className="w-full" data-id="element-1174">
                    <Share2 className="w-4 h-4 mr-2" data-id="element-1175" />
                    Bagikan
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Related Benefits */}
            <div data-id="element-1176">
              <h3 className="font-semibold text-neutral-900 mb-4" data-id="element-1177">
                Manfaat Lainnya
              </h3>
              <div className="space-y-4" data-id="element-1178">
                {relatedBenefits.map(item => <Card key={item.id} className="cursor-pointer hover:border-atios-primary transition-colors" onClick={() => navigate(`/benefits/${item.id}`)} data-id="element-1179">
                    <CardContent className="p-4 flex items-center gap-4" data-id="element-1180">
                      <div className="h-10 w-10 rounded-lg bg-atios-light flex items-center justify-center flex-shrink-0" data-id="element-1181">
                        <item.icon className="h-5 w-5 text-atios-primary" data-id="element-1182" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1183">
                        <h4 className="font-medium text-neutral-900 text-sm truncate" data-id="element-1184">
                          {item.title}
                        </h4>
                        <p className="text-xs text-neutral-500 capitalize" data-id="element-1185">
                          {item.category} • {item.discount}
                        </p>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}